// X63 (2026-10-19): the registry behind useSignalR's on/off, SSR-safe.
//
// useSignalR keeps its listeners in ONE module-level map: it has to, a page's
// components subscribe before the hub is connected and the dispatchers are
// re-registered after every reconnect. On the server that same module is shared
// by every request of the process and nothing ever unmounts, so a handler added
// during a server render pinned its whole render tree (the closure held the
// page's refs, the router, the nuxtApp: ~5.5 MB per signed-in /book render,
// never freed; MEMORY_M2). A server has no hub connection and no event will
// ever arrive, so a server-side on() is a no-op that hands back a no-op
// unsubscribe and, in dev, says which event asked.
//
// Pure: the Map is created per registry, isServer and warn are injectable, so
// the rule is unit-tested without a Nuxt runtime.

const defaultIsServer = () => import.meta.server === true;
const defaultWarn = (eventName) => {
  if (import.meta.dev) console.warn(`[useSignalR] on('${eventName}') during a server render: ignored. Subscribe in onMounted (or guard with import.meta.client).`);
};

/**
 * @param {{isServer?: () => boolean, warn?: (eventName: string) => void}} [options]
 */
export function createListenerRegistry({isServer = defaultIsServer, warn = defaultWarn} = {}) {
  const listeners = new Map(); // eventName -> Set of handlers

  /**
   * Adds a handler. Returns the matching unsubscribe. `onFirst(eventName)` runs
   * when the event gets its first handler (the hub dispatcher goes on then).
   */
  const on = (eventName, handler, {onFirst} = {}) => {
    if (isServer()) {
      warn(eventName);
      return () => {};
    }
    let set = listeners.get(eventName);
    if (!set) {
      set = new Set();
      listeners.set(eventName, set);
      onFirst?.(eventName);
    }
    set.add(handler);
    return () => off(eventName, handler);
  };

  /** Removes one handler, or every handler of the event when none is given. */
  const off = (eventName, handler) => {
    if (isServer()) return;
    const set = listeners.get(eventName);
    if (!set) return;
    if (handler) set.delete(handler);
    else set.clear();
  };

  const handlersFor = (eventName) => listeners.get(eventName) ?? new Set();
  const eventNames = () => [...listeners.keys()];
  const count = (eventName) => (eventName ? handlersFor(eventName).size : [...listeners.values()].reduce((n, s) => n + s.size, 0));

  return {on, off, handlersFor, eventNames, count};
}
